import React, { useEffect } from 'react';
import '../css/Roadmap.css';

const Roadmap = () => {

  // Fonction debounce pour limiter les appels fréquents
  const debounce = (func, wait) => {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  };

  // Fonction pour ajuster la hauteur de la fenêtre (limité aux appareils mobiles)
  useEffect(() => {
    const adjustHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    adjustHeight();

    const debouncedResize = debounce(adjustHeight, 200); // Limite l'appel lors des redimensionnements
    window.addEventListener('resize', debouncedResize);

    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  // Fonction pour scroller vers une section spécifique
  const scrollToSection = (id) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      window.history.pushState(null, '', `#${id}`);
    }
  };

  // Fonction pour gérer les clics dans le menu de navigation
  const handleMenuClick = (event, id) => {
    event.preventDefault();
    scrollToSection(id);
  };

  // Fonction pour mettre à jour la section active dans le menu
  const updateActiveSection = (id) => {
    const navItems = document.querySelectorAll('.roadmap-nav li');
    navItems.forEach((item) => item.classList.remove('roadmap-active'));

    const activeItem = Array.from(navItems).find((item) =>
      item.querySelector(`a[href="#${id}"]`)
    );
    if (activeItem) {
      activeItem.classList.add('roadmap-active');
    }
  };

  // Fonction pour déterminer la section active en fonction de la date actuelle
  const getCurrentSectionId = () => {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;
    let sectionId;

    if (year === 2024) {
      if (month <= 3) sectionId = 'section1'; // Q1 2024
      else if (month <= 6) sectionId = 'section2'; // Q2 2024
      else if (month <= 9) sectionId = 'section3'; // Q3 2024
      else sectionId = 'section4'; // Q4 2024
    } else if (year === 2025) {
      if (month <= 3) sectionId = 'section5'; // Q1 2025
      else if (month <= 6) sectionId = 'section6'; // Q2 2025
      else sectionId = 'section7'; // Q3-4 2025
    } else {
      sectionId = 'section1';
    }

    return sectionId;
  };

  useEffect(() => {
    const sections = document.querySelectorAll('.roadmap-section');
    const observerOptions = {
      root: null,
      threshold: 0.75, // Augmentation du seuil pour réduire le nombre de déclenchements
    };

    // Utilisation de requestAnimationFrame pour ajuster les sections uniquement lorsque nécessaire
    sections.forEach(section => {
      function adjustSection() {
        requestAnimationFrame(() => {
          if (section.scrollHeight <= window.innerHeight) {
            section.classList.add('center-vertically');  
          } else {
            section.classList.remove('center-vertically'); 
          }
        });
      }

      adjustSection();

      const debouncedAdjustSection = debounce(adjustSection, 200); // Limiter l'appel lors du resize
      window.addEventListener('resize', debouncedAdjustSection);
    });

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id;
          updateActiveSection(sectionId);
          window.history.pushState(null, '', `#${sectionId}`);
        }
      });
    }, observerOptions);

    sections.forEach((section) => observer.observe(section));

    // Initialisation avec la section courante basée sur la date
    const timeoutId = setTimeout(() => {
      const initialSectionId = getCurrentSectionId();
      if (initialSectionId) {
        scrollToSection(initialSectionId);
        updateActiveSection(initialSectionId);
      }
    }, 500); // Délai légèrement réduit pour une initialisation plus rapide

    return () => {
      if (observer) {
        sections.forEach((section) => observer.unobserve(section));
      }
      clearTimeout(timeoutId);
    };
  }, []);

  // Fonction pour centrer les éléments actifs sur les petits écrans
  useEffect(() => {
    const navLinks = document.querySelectorAll('.roadmap-nav a');

    const centerActiveLink = () => {
      const activeLink = document.querySelector('.roadmap-active');
      if (activeLink && window.innerWidth <= 768) {
        const container = document.querySelector('.roadmap-nav');
        const containerWidth = container.offsetWidth;
        const linkOffset = activeLink.offsetLeft;
        const linkWidth = activeLink.offsetWidth;

        container.scrollLeft = linkOffset - (containerWidth / 2) + (linkWidth / 2);
      }
    };

    navLinks.forEach((link) => {
      link.addEventListener('click', centerActiveLink);
    });

    return () => {
      navLinks.forEach((link) => {
        link.removeEventListener('click', centerActiveLink);
      });
    };
  }, []);

  return (
    <div className="roadmap-wrapper">
      <nav className="roadmap-nav-wrapper" id="navbar-example">
        <ul className="roadmap-nav">
          <li role="presentation">
            <a href="#section1" onClick={(e) => handleMenuClick(e, 'section1')}>
              <span className="roadmap-nav-counter">Q1 2024</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Project Kickoff</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section2" onClick={(e) => handleMenuClick(e, 'section2')}>
              <span className="roadmap-nav-counter">Q2 2024</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Expanding the Ecosystem</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section3" onClick={(e) => handleMenuClick(e, 'section3')}>
              <span className="roadmap-nav-counter">Q3 2024</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Strengthening the Ecosystem</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section4" onClick={(e) => handleMenuClick(e, 'section4')}>
              <span className="roadmap-nav-counter">Q4 2024</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Scaling and Strategic Growth</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section5" onClick={(e) => handleMenuClick(e, 'section5')}>
              <span className="roadmap-nav-counter">Q1 2025</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Strengthening Foundations</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section6" onClick={(e) => handleMenuClick(e, 'section6')}>
              <span className="roadmap-nav-counter">Q2 2025</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Transition to Innovation</p>
            </a>
          </li>
          <li role="presentation">
            <a href="#section7" onClick={(e) => handleMenuClick(e, 'section7')}>
              <span className="roadmap-nav-counter">Q3-4 2025</span>
              <div className="roadmap-vertical-line"></div>
              <div className="roadmap-horizontal-line"></div>
              <p className="roadmap-nav-body">Finalizing the Vision</p>
            </a>
          </li>
        </ul>
      </nav>

      <section className="roadmap-section section1" id="section1">
  <div className="section-content">
    <h2 className="section-title">Q1 2024 - Project Kickoff</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">Website Development <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Building a responsive and user-centric website to support upcoming features.
        </p>
      </li>
      <li>
        <h3 className="step-title">Marketing Content & Communication Setup <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Creating engaging marketing assets and setting up communication channels to reach our audience effectively.
        </p>
      </li>
      <li>
        <h3 className="step-title">Marketing Plan Drafting <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Developing a strategic marketing plan with targeted campaigns and outreach programs.
        </p>
      </li>
      <li>
        <h3 className="step-title">Team Recruitment <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Onboarding a skilled communication and network management team for streamlined operations.
        </p>
      </li>
      <li>
        <h3 className="step-title">Blockchain Development <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Initiating the core blockchain architecture, focusing on security and scalability.
        </p>
      </li>
    </ul>
  </div>
</section>

<section className="roadmap-section section2" id="section2">
  <div className="section-content">
    <h2 className="section-title">Q2 2024 - Expanding the Ecosystem</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">Launch of Twitter & Discord Channels <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Establishing vibrant communities on Twitter and Discord to keep our users engaged, informed, and involved in the project's progress.
        </p>
      </li>
      <li>
        <h3 className="step-title">Mainnet Launch <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Deploying the mainnet, offering a stable and scalable blockchain network for users to start participating in the ecosystem.
        </p>
      </li>
      <li>
        <h3 className="step-title">Release of Cross-Platform Wallets <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Launching secure and user-friendly wallets for Windows, Linux, and macOS, enabling users on all major platforms to safely manage their digital assets.
        </p>
      </li>
      <li>
        <h3 className="step-title">Explorer Launch <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Introducing a blockchain explorer to provide transparency, allowing users to track transactions and network activity in real-time.
        </p>
      </li>
      <li>
        <h3 className="step-title">First Mining Pool <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Setting up the first mining pool, enabling users to contribute their computing power and earn rewards.
        </p>
      </li>
      <li>
        <h3 className="step-title">Exchange Listings <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Securing listings on the first and second exchanges, providing liquidity and multiple trading options for users to buy, sell, and trade with ease.
        </p>
      </li>
    </ul>
  </div>
</section>
<section className="roadmap-section section3" id="section3">
  <div className="section-content">
    <h2 className="section-title">Q3 2024 - Strengthening the Ecosystem</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">Launch of the Web Wallet <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Introducing a secure, browser-based wallet that allows users to manage their digital assets with enhanced security features and ease of access, directly from their web browsers.
        </p>
      </li>
      <li>
        <h3 className="step-title">Expansion of Mining Pools <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Launching additional mining pools to improve network security and provide more options for users to participate and earn rewards, ensuring a more decentralized mining process.
        </p>
      </li>
      <li>
        <h3 className="step-title">Additional Exchange Listings <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Securing listings on more exchanges to broaden market reach, increase liquidity, and make it easier for users to access, buy, and trade the token globally.
        </p>
      </li>
      <li>
        <h3 className="step-title">Website Enhancements <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Updating and refining the website with new features, improved user experience, and optimized content to better serve our growing community and new users.
        </p>
      </li>
      <li>
        <h3 className="step-title">Strategic Adjustments <span style={{ fontSize: '24px'}}>☑</span></h3>
        <p className="step-description">
          Analyzing market trends and user feedback to make strategic adjustments, ensuring the project's direction aligns with user needs and market opportunities for sustained growth.
        </p>
      </li>
    </ul>
  </div>
</section>
<section className="roadmap-section section4" id="section4">
  <div className="section-content">
    <h2 className="section-title">Q4 2024 - Scaling and Strategic Growth</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">Increase in Development Team Capacity</h3>
        <p className="step-description">
          Expanding our development teams to boost productivity, enhance features, and accelerate the implementation of key updates and innovations.
        </p>
      </li>
      <li>
        <h3 className="step-title">Initiation of the POUW Model Development</h3>
        <p className="step-description">
          Creating a Proof of Useful Work (POUW) model to enhance the efficiency of our blockchain network, allowing participants to contribute computational power to meaningful tasks.
        </p>
      </li>
      <li>
        <h3 className="step-title">Acceleration of Communication & Marketing Efforts</h3>
        <p className="step-description">
          Boosting marketing campaigns and communication efforts to increase project visibility, attract new users, and strengthen community engagement across all platforms.
        </p>
      </li>
      <li>
        <h3 className="step-title">Participation in Blockchain Events</h3>
        <p className="step-description">
          Engaging in key blockchain events to showcase our project, connect with industry leaders, and build valuable partnerships to drive the project's future growth.
        </p>
      </li>
      <li>
        <h3 className="step-title">Partner Search</h3>
        <p className="step-description">
          Actively seeking strategic partnerships to expand our ecosystem, enhance our technology stack, and provide users with a more robust and interconnected platform.
        </p>
      </li>
    </ul>
  </div>
</section>
<section className="roadmap-section section5" id="section5">
  <div className="section-content">
    <h2 className="section-title">Q1 2025 - Strengthening Foundations</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">First Official Webinar</h3>
        <p className="step-description">
          Hosting our inaugural webinar to engage with the community, providing insights into the project's progress and future plans.
        </p>
      </li>
      <li>
        <h3 className="step-title">Detailed Architecture Presentation</h3>
        <p className="step-description">
          Offering an in-depth explanation of the system architecture, showcasing the technical backbone of our blockchain and how it ensures security and scalability.
        </p>
      </li>
      <li>
        <h3 className="step-title">Formalization of Strategic Partnerships</h3>
        <p className="step-description">
          Finalizing agreements with key partners to enhance our ecosystem, bringing new expertise, resources, and opportunities to the network.
        </p>
      </li>
      <li>
        <h3 className="step-title">Tier 1 Exchange Listing</h3>
        <p className="step-description">
          Listing on a Tier 1 exchange, providing greater liquidity and accessibility for global users, and reinforcing our presence in the market.
        </p>
      </li>
      <li>
        <h3 className="step-title">Enhanced Explorer Features</h3>
        <p className="step-description">
          Upgrading the blockchain explorer with advanced analytics and user-friendly tools, allowing deeper insights into the network's activities.
        </p>
      </li>
    </ul>
  </div>
</section>
<section className="roadmap-section section6" id="section6">
  <div className="section-content">
    <h2 className="section-title">Q2 2025 - Transition to Innovation</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">BETA Launch of the Platform</h3>
        <p className="step-description">
          Initiating the BETA phase of our platform, inviting early users to experience the core features, test the functionalities, and provide valuable feedback before the official launch.
        </p>
      </li>
      <li>
        <h3 className="step-title">Migration from POW to POUW Protocol</h3>
        <p className="step-description">
          Transitioning from the energy-intensive Proof of Work (POW) to the more sustainable Proof of Useful Work (POUW), optimizing resource usage and boosting network efficiency.
        </p>
      </li>
      <li>
        <h3 className="step-title">Integration of Hardware Wallet Support</h3>
        <p className="step-description">
          Introducing support for hardware wallets to ensure enhanced security and provide users with the ability to store their digital assets offline.
        </p>
      </li>
      <li>
        <h3 className="step-title">Smart Development Solutions</h3>
        <p className="step-description">
          Implementing innovative development solutions to streamline the integration of third-party applications and enhance the platform’s adaptability to user needs.
        </p>
      </li>
      <li>
        <h3 className="step-title">Expanding Platform Capabilities</h3>
        <p className="step-description">
          Continuously adding new features and functionalities to enrich the user experience and keep the platform ahead in the rapidly evolving blockchain ecosystem.
        </p>
      </li>
    </ul>
  </div>
</section>
<section className="roadmap-section section7" id="section7">
  <div className="section-content">
    <h2 className="section-title">Q3-Q4 2025 - Finalizing the Vision</h2>
    <ul className="roadmap-steps">
      <li>
        <h3 className="step-title">Official Launch of the Platform</h3>
        <p className="step-description">
          After extensive testing and user feedback from the BETA phase, the platform is now ready for its official launch.
        </p>
      </li>
      <li>
        <h3 className="step-title">Optimized Transition to POUW Protocol</h3>
        <p className="step-description">
          Completing the transition from Proof of Work (POW) to the innovative Proof of Useful Work (POUW). This shift optimizes energy consumption and repurposes computational resources towards meaningful tasks, setting a new standard in blockchain efficiency and sustainability.
        </p>
      </li>
      <li>
        <h3 className="step-title">Introduction of Smart Development Solutions</h3>
        <p className="step-description">
          Unveiling a suite of advanced development tools that empower developers to create, deploy, and manage decentralized applications (dApps) with ease.
        </p>
      </li>
      <li>
        <h3 className="step-title">Continuous Expansion and Ecosystem Growth</h3>
        <p className="step-description">
          Focusing on scaling the ecosystem with strategic partnerships, continuous feature updates, and community-driven improvements.
        </p>
      </li>
    </ul>
  </div>
</section>

    </div>
  );
};

export default Roadmap;
